"use client";

import React, { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function Counter({
  end,
  suffix,
  label
}: {
  end: number;
  suffix: string;
  label: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const fps = 60;
    const increment = end / (duration / (1000 / fps));
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.round(start));
      }
    }, 1000 / fps);
    return () => clearInterval(timer);
  }, [isVisible, end]);
  return <div ref={ref} className="flex flex-col items-center justify-center text-center p-4 md:p-8">
      <div className="text-6xl md:text-7xl lg:text-8xl font-serif font-semibold text-primary mb-4 tracking-tighter leading-none">
        {count.toLocaleString()}
        {suffix}
      </div>
      <div className="text-xs md:text-sm font-mono text-slate-500 uppercase tracking-[0.15em] md:tracking-[0.2em]">
        {label}
      </div>
    </div>;
}
export default function ExplorationStats() {
  const stats = [{
    value: 1240,
    suffix: "",
    label: "Miles Explored"
  }, {
    value: 452,
    suffix: "",
    label: "Coffees Consumed"
  }, {
    value: 89,
    suffix: "",
    label: "Stories Shared"
  }, {
    value: 15,
    suffix: "k",
    label: "Daily Readers"
  }];
  return <section data-section-id="221504" id="exploration-stats" className="bg-secondary/50 py-20 md:py-32 overflow-hidden border-y border-border/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-y-12 md:gap-y-0 w-full">
          {stats.map((stat, i) => <div key={i} data-index={i} className="contents">
              <div className="w-full flex justify-center">
                <Counter end={stat.value} suffix={stat.suffix} label={stat.label} />
              </div>
              {i < stats.length - 1 && <div className="flex justify-center items-center w-full md:w-auto md:h-32">
                  {}
                  <Separator orientation="horizontal" className="md:hidden w-24 bg-primary/20" />
                  {}
                  <Separator orientation="vertical" className="hidden md:block h-full bg-primary/20" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}